import type { AdminContact, Paginated } from "@lucky-try/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog } from "../components/ui/dialog";
import { Field, Input, Select, Textarea } from "../components/ui/form";
import {
  Badge,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Pagination,
  Toolbar,
  type Tone,
} from "../components/ui/misc";
import { api, errorMessage } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useCan } from "../lib/session";

const tones: Record<AdminContact["status"], Tone> = {
  new: "warning",
  in_progress: "info",
  closed: "success",
  spam: "danger",
};

export function ContactPage() {
  const search = useSearch({ from: "/authed/contact" });
  const navigate = useNavigate({ from: "/contact" });
  const queryClient = useQueryClient();
  const canWrite = useCan("feedback.write");
  const [term, setTerm] = useState(search.q ?? "");
  const [open, setOpen] = useState<AdminContact | null>(null);

  const contacts = useQuery({
    queryKey: ["contact", search],
    queryFn: () =>
      api<Paginated<AdminContact>>("/admin/contact", { query: { ...search, pageSize: 25 } }),
  });

  const update = useMutation({
    mutationFn: (message: AdminContact) =>
      api<void>(`/admin/contact/${message.id}`, {
        method: "PATCH",
        body: { status: message.status, adminNote: message.adminNote },
      }),
    onSuccess: async () => {
      setOpen(null);
      await queryClient.invalidateQueries({ queryKey: ["contact"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Message updated");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (contacts.error)
    return <ErrorState error={contacts.error} onRetry={() => void contacts.refetch()} />;

  return (
    <>
      <PageHeader title="Contact" description="Messages from the company page form." />

      <Toolbar>
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({
              search: (previous) => ({ ...previous, page: 1, q: term || undefined }),
            });
          }}
        >
          <Input
            placeholder="Search message or sender"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            className="w-64"
          />
          <Button type="submit">Search</Button>
        </form>
        <Select
          value={search.status ?? ""}
          aria-label="Status filter"
          onChange={(event) =>
            void navigate({
              search: (previous) => ({
                ...previous,
                page: 1,
                status: (event.target.value || undefined) as typeof search.status,
              }),
            })
          }
        >
          <option value="">All statuses</option>
          <option value="new">New</option>
          <option value="in_progress">In progress</option>
          <option value="closed">Closed</option>
          <option value="spam">Spam</option>
        </Select>
      </Toolbar>

      <div className="border-line bg-surface rounded-card overflow-hidden border">
        {contacts.isPending ? (
          <Loading />
        ) : contacts.data.items.length === 0 ? (
          <EmptyState>No messages.</EmptyState>
        ) : (
          <ul>
            {contacts.data.items.map((message) => (
              <li key={message.id} className="t-row border-line/60 border-b last:border-0">
                <button
                  type="button"
                  onClick={() => setOpen(message)}
                  className="w-full px-4 py-3 text-left"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={tones[message.status]}>{message.status.replace("_", " ")}</Badge>
                    <span className="text-ink text-ui font-medium">{message.name}</span>
                    <span className="text-muted text-caption">{message.contact}</span>
                    <span className="text-muted text-caption ml-auto">
                      {formatDateTime(message.createdAt)}
                    </span>
                  </div>
                  <p className="text-body text-caption mt-1 line-clamp-2">{message.message}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
        <Pagination
          page={contacts.data?.page ?? 1}
          pageSize={contacts.data?.pageSize ?? 25}
          total={contacts.data?.total ?? 0}
          onPage={(page) => void navigate({ search: (previous) => ({ ...previous, page }) })}
        />
      </div>

      <Dialog
        open={open !== null}
        onClose={() => setOpen(null)}
        width="lg"
        eyebrow="Contact message"
        title={open?.name ?? "Message"}
        footer={
          canWrite && (
            <Button
              variant="primary"
              disabled={update.isPending}
              onClick={() => open && update.mutate(open)}
            >
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          )
        }
      >
        {open && (
          <div className="text-caption flex flex-col gap-3">
            <p className="border-line bg-raised text-body text-ui rounded border p-3 whitespace-pre-wrap">
              {open.message}
            </p>
            <dl className="text-muted grid grid-cols-[110px_1fr] gap-y-1">
              <dt>Reply to</dt>
              <dd className="text-body">{open.contact}</dd>
              <dt>Country</dt>
              <dd className="text-body">{open.country ?? "—"}</dd>
              <dt>Received</dt>
              <dd className="text-body">{formatDateTime(open.createdAt)}</dd>
            </dl>
            <Field label="Status">
              <Select
                value={open.status}
                disabled={!canWrite}
                onChange={(event) =>
                  setOpen({ ...open, status: event.target.value as AdminContact["status"] })
                }
              >
                <option value="new">New</option>
                <option value="in_progress">In progress</option>
                <option value="closed">Closed</option>
                <option value="spam">Spam</option>
              </Select>
            </Field>
            <Field label="Internal note">
              <Textarea
                rows={3}
                disabled={!canWrite}
                value={open.adminNote ?? ""}
                onChange={(event) => setOpen({ ...open, adminNote: event.target.value || null })}
              />
            </Field>
          </div>
        )}
      </Dialog>
    </>
  );
}
